// src/components/billing/billingShared.js
//
// Formatting + status language shared by the client Billing page, the admin
// Billing page and the invoice drawer. Status keys come from the server
// (billing/invoices.py status_key) - one place decides what "overdue" means.

import { apiFetch } from '../../api/apiClient';
import { saveBlobFile } from '../../utils/saveFile';

// Same 3-colour status language as fleet-theme.css:
//   turquoise = fine, neon = needs attention, inverted white chip = critical.
export const STATUS_META = {
  paid:       { label: 'Paid',        cls: 'ft-badge-online' },
  processing: { label: 'Processing',  cls: 'ft-badge-online bl-badge-pulse' },
  open:       { label: 'Due',         cls: 'ft-badge-degraded' },
  partial:    { label: 'Part paid',   cls: 'ft-badge-degraded' },
  overdue:    { label: 'Overdue',     cls: 'ft-badge-down' },
  draft:      { label: 'Draft',       cls: 'ft-badge-unknown' },
  void:       { label: 'Void',        cls: 'ft-badge-unknown' },
};

export function StatusBadge({ status, daysOverdue }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', cls: 'ft-badge-unknown' };
  return (
    <span className={`ft-badge ${meta.cls}`}>
      {meta.label}
      {status === 'overdue' && daysOverdue > 0 ? ` ${daysOverdue}d` : ''}
    </span>
  );
}

const moneyFormatters = {};
export function formatMoney(amount, currency) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return '—';
  const cur = (currency || '').toUpperCase();
  if (!cur || cur === '—') return Number(amount).toFixed(2);
  if (!moneyFormatters[cur]) {
    try {
      moneyFormatters[cur] = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cur,
        currencyDisplay: 'code',
      });
    } catch {
      moneyFormatters[cur] = { format: (n) => `${cur} ${Number(n).toFixed(2)}` };
    }
  }
  // Intl puts a non-breaking space after the code; keep it.
  return moneyFormatters[cur].format(Number(amount));
}

export function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(String(iso).length <= 10 ? `${iso}T00:00:00` : String(iso).replace(' ', 'T') + (String(iso).includes('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(String(iso).replace(' ', 'T') + (String(iso).includes('Z') || String(iso).includes('+') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** The invoice PDF (Xero's own, fetched through our server). */
export async function fetchInvoicePdf(url) {
  const res = await apiFetch(url);
  if (!res.ok) {
    let msg = 'Could not load the PDF';
    try { msg = (await res.json()).error || msg; } catch {}
    throw new Error(msg);
  }
  return res.blob();
}

export const pdfFileName = (invoiceNumber) =>
  `${String(invoiceNumber || 'invoice').replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`;

/** Download the PDF. Browser: normal download. App: share sheet. */
export async function downloadInvoicePdf(url, invoiceNumber) {
  const blob = await fetchInvoicePdf(url);
  await saveBlobFile(blob, pdfFileName(invoiceNumber), { dialogTitle: 'Save invoice' });
}

export const LoadingScreen = ({ text }) => (
  <div className="cm-loading-overlay">
    <div className="cm-spinner-container">
      <video
        autoPlay loop muted playsInline preload="auto"
        poster={process.env.PUBLIC_URL + '/Logo_video_poster.jpg'}
        className="spinner-video" width="280" height="280"
      >
        <source src={process.env.PUBLIC_URL + '/Logo_video.mp4'} type="video/mp4" />
      </video>
      <p>{text}</p>
    </div>
  </div>
);